import { Alert, Box, Button, Chip, FormControl, Grid, Stack, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import { Interval } from "luxon";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchEmployee } from "../api/employee";
import { addTempAssignmentEvent, editTempAssignmentEvent, getTAEDEventsByReferenceNumber } from "../api/taed";
import { isUserAuthorizedByServer } from "../api/userAccess";
import BaseScheduleDialog from "../components/BaseScheduleDialog";
import PageLoading from "../components/PageLoading";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import ConfirmationDialog from "../components/fundamentals/ConfirmationDialog";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import StarsDatePicker from "../components/fundamentals/StarsDatePicker";
import StarsSelectInput from "../components/fundamentals/StarsSelectInput";
import SuccessDialog from "../components/fundamentals/SuccessDialog";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import { copyJsonObject, getFormattedPhoneNumber, getMultiStationAdminStationList, sortOnCaseSensitiveTexts } from "../util/commonUtils";
import {
  BREADCRUMBS_TITLE,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  CLIENT,
  FREEFORM_REASON_CHAR_LIMIT_FOR_TEMP_ASSGNMENT,
  HOME_PATH,
  MULTI_STATION_ADMIN_ACCESS,
  WEEKDAYS_SHORT,
} from "../util/constants";
import { TWO_MONTHS_IN_PAST } from "../util/dateHelper";
import { convert_Luxon_To_DateFns } from "../util/dateTimeFormatter";
import { getUserDataFromEmployeeService } from "../util/employeeDataRetriever";
import { validateEmployee, validateEmployeeId, validatePhoneNumber } from "../util/inputValidation";
import {
  ADMIN_MOBILE_WARNING_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  BASE_SCHEDULE_REQUIRES_EMPLOYEE_ID,
  INVALID_EMPLOYEE_ID,
  REFERENCE_NUMBER_SUB_TITLE,
  STATION_FILTER_MUST_BE_SELECTED,
  TEMP_ASSIGNMENT_CLOCK_LOCATION_MESSAGE,
  TEMP_ASSIGNMENT_CONFIRMATION_TITLE,
  TEMP_ASSIGNMENT_CREATE_CONFIRM_MESSAGE,
  TEMP_ASSIGNMENT_CREATE_SUCCESS_MESSAGE,
  TEMP_ASSIGNMENT_EDIT_CONFIRM_MESSAGE,
  TEMP_ASSIGNMENT_EDIT_SUCCESS_MESSAGE,
  TEMP_ASSIGNMENT_SUBMISSION_ERROR_MESSAGE,
  TEMP_ASSIGNMENT_EDIT_WARNING_MESSAGE,
  WARNING_ALERT_TITLE,
  TEMP_ASSIGNMENT_ACTION_ERROR_TITLE,
  CONTACT_NUMBER_REQUIRED,
  INVALID_CONTACT_NUMBER,
} from "../util/messageConstants";
import {
  baseScheduleApplyToAllFilterInitialState,
  baseScheduleErrorsInitialState,
  baseScheduleInitialState,
  employeeInfoErrorsInitialState,
} from "../util/taedUtils";
import {
  baseScheduleDailyValidation,
  isDayIncludedInAssignment,
  validateEmployeeDataForTemporaryAssignment,
  validateNoPartialSchedules,
  validateOverlappingTAED,
  validateTAEDComments,
  validateTAEDEndDate,
  validateTAEDStartDate,
} from "../util/temporaryAssignmentInputValidation";
import { getUnionContractGivenJobCode, getWorkgroupGivenJobCode } from "../util/workgroupMapper";
import TemporaryAssignmentAuditHistoryDialog from "../components/taed/TemporaryAssignmentAuditHistoryDialog";

const useStyles = makeStyles((theme) => ({
  inputText: {
    width: "100%",
    textAlign: "left",
    paddingRight: "10px",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  inputSelect: {
    textAlign: "left",
    marginRight: "10px",
  },
  employeeDataTitle: {
    marginTop: "7px",
    color: theme.palette.secondary.swaGray,
  },
  employeeDataValue: {
    fontSize: "18px",
    marginTop: "-5px",
  },
  referenceNumber: {
    background: theme.palette.secondary.swaGray3,
    marginBottom: "10px",
    float: "left",
  },
}));

const CreateTemporaryAssignmentPage = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);

  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  const [employeeID, setEmployeeId] = useState("");
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeIdErrors, setEmployeeIdErrors] = useState("");
  const [employeeIdSearchLoaded, setEmployeeIdSearchLoaded] = useState(false);
  const [employeeIdSearchTriggered, setEmployeeIdSearchTriggered] = useState(false);
  const [contactNumber, setContactNumber] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [comments, setComments] = useState("");
  const [isValidationLoading, setIsValidationLoading] = useState(false);

  //Edit Temp Assignment
  const [inputDisable, setInputDisable] = useState(false);
  const [isEditTempAssignment, setIsEditTempAssignment] = useState(false);
  const [employeeInfoErrors, setEmployeeInfoErrors] = useState(employeeInfoErrorsInitialState);
  const [baseSchedule, setBaseSchedule] = useState(baseScheduleInitialState);
  const [baseScheduleErrors, setBaseScheduleErrors] = useState(baseScheduleErrorsInitialState);
  const [isCancelWarningOpen, setIsCancelWarningOpen] = useState(false);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  // Submit Dialog
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isErrorAddTempAssignment, setIsErrorAddTempAssignment] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [isAddTempAssignmentLoading, setIsAddTempAssignmentLoading] = useState(false);

  const location = useLocation();

  // Audit History
  const [isAuditHistoryDialogOpen, setIsAuditHistoryDialogOpen] = useState(false);
  const [tempAssignmentEvents, setTempAssignmentEvents] = useState("");
  const [isLoading, setLoading] = useState(false);

  // Apply To All Filter (Base Schedule)
  const [applyToAllFilterData, setApplyToAllFilterData] = useState(
    copyJsonObject(baseScheduleApplyToAllFilterInitialState)
  );

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      if (employeeData?.homeStation === stationFilter) {
        setEmployeeIdErrors("Employee must be in different Station");
        setEmployeeIdSearchTriggered(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeData, stationFilter]);

  useEffect(() => {
    setExcludedAssignmentDays(baseSchedule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateRef.current, endDateRef.current]);

  useEffect(() => {
    if (location.state) {
      setIsEditTempAssignment(true);
      setInputDisable(true);
      setReferenceNumber(location.state.id);
      setEmployeeId(location.state.eid);
      fetchEmployeeData(location.state.eid);
      setStartDate(location.state.startDate);
      setEndDate(location.state.endDate);
      setContactNumber(location.state.contactNumber);
      setBaseSchedule(location.state.baseSchedule);
      endDateRef.current = location.state.endDate;
      startDateRef.current = location.state.startDate;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isAdmin);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const handleEmployeeIdChange = (eid) => {
    if (employeeData) {
      setEmployeeData(null);
    }
    setEmployeeIdErrors("");
    setEmployeeId(eid.target.value.toLowerCase().trim());
  };

  const clearTempAssignment = () => {
    setIsSuccessDialogOpen(false);
    setIsSubmitDialogOpen(false);
    setIsCancelWarningOpen(false);
    setBaseSchedule(copyJsonObject(baseScheduleInitialState));
    setEmployeeId("");
    setContactNumber("");
    setStartDate(null);
    setEndDate(null);
    setComments("");
    setEmployeeData(null);
    setEmployeeIdErrors("");
    setEmployeeInfoErrors(copyJsonObject(employeeInfoErrorsInitialState));
    setBaseScheduleErrors(copyJsonObject(baseScheduleErrorsInitialState));
  };

  const fetchEmployeeData = (eid) => {
    setEmployeeIdSearchLoaded(false);
    setEmployeeIdSearchTriggered(true);
    setEmployeeData(null);
    setEmployeeIdErrors("");
    let employeeDataErrors = "";

    fetchEmployee(eid)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          const employeeData = await getUserDataFromEmployeeService(result);

          employeeDataErrors = validateEmployeeDataForTemporaryAssignment(employeeData, userSession.eid);
          if (employeeDataErrors !== "") {
            setEmployeeIdErrors(employeeDataErrors);
            setEmployeeIdSearchLoaded(true);
          } else {
            employeeDataErrors = validateEmployee(
              employeeData.eid,
              employeeData.firstName,
              employeeData.lastName,
              employeeData.homeStation,
              getWorkgroupGivenJobCode(employeeData.jobCode)
            );
            if (employeeDataErrors !== "") {
              setEmployeeIdErrors(INVALID_EMPLOYEE_ID);
              setEmployeeIdSearchLoaded(true);
            } else {
              setEmployeeData(employeeData);
              setEmployeeIdSearchLoaded(true);
            }
          }

          if (!location.state) {
            setContactNumber(employeeData.phoneNumber || "");
          }
        } else {
          setEmployeeIdErrors(INVALID_EMPLOYEE_ID);
          setEmployeeIdSearchTriggered(false);
        }
      });
  };

  const handleStationFilterChange = (event) => {
    clearTempAssignment();
    dispatch(updateStationFilter(event.target.value));
  };

  const handleCommentsChange = (comments) => {
    setComments(comments.target.value);
  };
  const handleCommentsBlur = () => {
    validateComments();
  };

  const handleEmployeeIdBlur = (event) => {
    let trimEmpId = event.target.value.trim();
    let errors = validateEmployeeId(trimEmpId);
    if (employeeIdErrors === "" || errors !== "") {
      setEmployeeIdErrors(errors);
    }
    if (errors === "") {
      if (!employeeData || trimEmpId !== employeeData.eid) {
        fetchEmployeeData(trimEmpId);
      }
    }
  };

  const handleEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleEmployeeIdBlur(event);
    }
  };

  const handleContactNumberChange = (event) => {
    setContactNumber(event.target.value);
  };
  const handleContactNumberBlur = () => {
    validateContactNumber();
  };

  const handleStartDateChange = (date) => {
    startDateRef.current = date;
    setStartDate(date);
    validateStartDate();
    validateEndDate();
  };

  const handleEndDateChange = (date) => {
    endDateRef.current = date;
    setEndDate(date);
    validateStartDate();
    validateEndDate();
  };

  const setExcludedAssignmentDays = (schedule) => {
    let currentSchedule = copyJsonObject(schedule);
    if (startDateRef.current !== null && endDateRef.current !== null) {
      const tempAssignmentLengthInDays = Interval.fromDateTimes(startDateRef.current, endDateRef.current).length(
        "days"
      );
      if (tempAssignmentLengthInDays < 7) {
        let excludedAssignmentDays = getExcludedAssignmentDays(startDateRef.current, endDateRef.current);
        for (const day in currentSchedule) {
          if (currentSchedule[day].isNA === true && !excludedAssignmentDays.includes(day)) {
            currentSchedule[day].start.hr = "";
            currentSchedule[day].start.min = "";
            currentSchedule[day].end.hr = "";
            currentSchedule[day].end.min = "";
            currentSchedule[day].isOFF = false;
            currentSchedule[day].isNA = false;
          }
        }

        excludedAssignmentDays.forEach((excludedDay) => {
          currentSchedule[excludedDay].start.hr = "N/A";
          currentSchedule[excludedDay].start.min = "N/A";
          currentSchedule[excludedDay].end.hr = "N/A";
          currentSchedule[excludedDay].end.min = "N/A";
          currentSchedule[excludedDay].isOFF = false;
          currentSchedule[excludedDay].isNA = true;
        });
      } else {
        for (const day in currentSchedule) {
          if (currentSchedule[day].isNA === true) {
            currentSchedule[day].start.hr = "";
            currentSchedule[day].start.min = "";
            currentSchedule[day].end.hr = "";
            currentSchedule[day].end.min = "";
            currentSchedule[day].isOFF = false;
            currentSchedule[day].isNA = false;
          }
        }
      }
      setBaseSchedule(copyJsonObject(currentSchedule));
    }
  };

  const getExcludedAssignmentDays = (startDate, endDate) => {
    let excludedAssignmentDays = [];
    const startDateIdx = getDayOfWeekIndex(startDate);
    const endDateIdx = getDayOfWeekIndex(endDate);
    if (startDateIdx === endDateIdx) {
      excludedAssignmentDays = WEEKDAYS_SHORT.filter((day) => day !== getDayOfWeek(startDate));
    } else if (startDateIdx < endDateIdx) {
      const beginningOfWeek = WEEKDAYS_SHORT.slice(0, startDateIdx);
      const endOfWeek = WEEKDAYS_SHORT.slice(endDateIdx + 1);
      excludedAssignmentDays = beginningOfWeek.concat(endOfWeek);
    } else {
      excludedAssignmentDays = WEEKDAYS_SHORT.slice(endDateIdx + 1, startDateIdx);
    }
    return excludedAssignmentDays;
  };

  const getDayOfWeekIndex = (date) => {
    return WEEKDAYS_SHORT.indexOf(getDayOfWeek(date));
  };

  const getDayOfWeek = (date) => {
    return date?.weekdayShort?.toUpperCase();
  };

  const handleClearBaseSchedule = () => {
    let currentSchedule = copyJsonObject(baseSchedule);
    for (const day in currentSchedule) {
      if (!currentSchedule[day].isNA) {
        currentSchedule[day].start.hr = "";
        currentSchedule[day].start.min = "";
        currentSchedule[day].end.hr = "";
        currentSchedule[day].end.min = "";
        currentSchedule[day].isOFF = false;
        currentSchedule[day].isNA = false;
      }
    }

    setApplyToAllFilterData(copyJsonObject(baseScheduleApplyToAllFilterInitialState));
    setBaseSchedule(copyJsonObject(currentSchedule));
    setBaseScheduleErrors(copyJsonObject(baseScheduleErrorsInitialState));
  };
  const handleApplyToAll = () => {
    let currentSchedule = copyJsonObject(baseSchedule);
    for (const day in currentSchedule) {
      if (!currentSchedule[day].isNA && !currentSchedule[day].isOFF) {
        currentSchedule[day].start.hr = applyToAllFilterData.start.hr;
        currentSchedule[day].start.min = applyToAllFilterData.start.min;
        currentSchedule[day].end.hr = applyToAllFilterData.end.hr;
        currentSchedule[day].end.min = applyToAllFilterData.end.min;
        currentSchedule[day].isOFF = false;
        currentSchedule[day].isNA = false;
      }
    }
    setBaseSchedule(copyJsonObject(currentSchedule));
    setBaseScheduleErrors(copyJsonObject(baseScheduleErrorsInitialState));
  };

  const dayOffChecked = (day) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    currentSchedule[day].start.hr = "OFF";
    currentSchedule[day].start.min = "OFF";
    currentSchedule[day].end.hr = "OFF";
    currentSchedule[day].end.min = "OFF";
    currentSchedule[day].isOFF = true;
    setBaseSchedule(currentSchedule);
  };

  const dayOffUnchecked = (day) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    currentSchedule[day].start.hr = "";
    currentSchedule[day].start.min = "";
    currentSchedule[day].end.hr = "";
    currentSchedule[day].end.min = "";
    currentSchedule[day].isOFF = false;
    setBaseSchedule(currentSchedule);
  };

  const handleApplyToAllStartHourChange = (value) => {
    let currentFilter = copyJsonObject(applyToAllFilterData);
    currentFilter.start.hr = value;
    setApplyToAllFilterData(currentFilter);
  };
  const handleApplyToAllStartMinChange = (value) => {
    let currentFilter = copyJsonObject(applyToAllFilterData);
    currentFilter.start.min = value;
    setApplyToAllFilterData(currentFilter);
  };
  const handleApplyToAllEndHourChange = (value) => {
    let currentFilter = copyJsonObject(applyToAllFilterData);
    currentFilter.end.hr = value;
    setApplyToAllFilterData(currentFilter);
  };
  const handleApplyToAllEndMinChange = (value) => {
    let currentFilter = copyJsonObject(applyToAllFilterData);
    currentFilter.end.min = value;
    setApplyToAllFilterData(currentFilter);
  };

  const handleStartHourChange = (day, value) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    currentSchedule[day].start.hr = value;
    setBaseSchedule(currentSchedule);
  };

  const handleStartMinChange = (day, value) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    currentSchedule[day].start.min = value;
    setBaseSchedule(currentSchedule);
  };

  const handleEndHourChange = (day, value) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    currentSchedule[day].end.hr = value;
    setBaseSchedule(currentSchedule);
  };

  const handleEndMinChange = (day, value) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    currentSchedule[day].end.min = value;
    setBaseSchedule(currentSchedule);
  };

  const handleCheckedShiftOff = (event, day) => {
    if (event.target.checked) {
      dayOffChecked(day);
    } else {
      dayOffUnchecked(day);
    }
  };

  const addTempAssignment = async () => {
    setIsErrorAddTempAssignment(false);
    setIsAddTempAssignmentLoading(true);
    const payload = {
      eid: employeeID,
      homeStation: employeeData.homeStation,
      tempStation: stationFilter,
      firstName: employeeData.firstName,
      lastName: employeeData.lastName,
      workgroup: getWorkgroupGivenJobCode(employeeData.jobCode),
      ftPt: employeeData.ftpt,
      contactNumber: contactNumber,
      startDate: startDate,
      endDate: endDate,
      baseSchedule: copyJsonObject(baseSchedule),
      comments: comments,
      modifiedByEid: userSession.eid,
      modifiedByFirstName: userSession.firstName,
      modifiedByLastName: userSession.lastName,
      modifiedByWorkgroup: getWorkgroupGivenJobCode(userSession.jobCode),
      createdBy: CLIENT
    };
    await addTempAssignmentEvent(payload)
      .then((res) => res.json())
      .then(async (result) => {
        if (result.referenceNumber) {
          setReferenceNumber(result.referenceNumber);
          setIsAddTempAssignmentLoading(false);
          setIsSuccessDialogOpen(true);
          setIsSubmitDialogOpen(false);
        } else {
          setIsAddTempAssignmentLoading(false);
          setIsSuccessDialogOpen(true);
          setIsErrorAddTempAssignment(true);
          setIsSubmitDialogOpen(false);
        }
      })
      .catch((err) => {
        console.log(err, "error");
        setIsAddTempAssignmentLoading(false);
        setIsSuccessDialogOpen(true);
        setIsErrorAddTempAssignment(true);
        setIsSubmitDialogOpen(false);
      });
  };
  const editTempAssignment = async () => {
    setIsErrorAddTempAssignment(false);
    setIsAddTempAssignmentLoading(true);
    const payload = {
      referenceNumber: location.state.id,
      eid: employeeID,
      homeStation: employeeData.homeStation,
      tempStation: stationFilter,
      firstName: employeeData.firstName,
      lastName: employeeData.lastName,
      workgroup: getWorkgroupGivenJobCode(employeeData.jobCode),
      ftPt: employeeData.ftpt,
      contactNumber: contactNumber,
      startDate: startDate,
      endDate: endDate,
      baseSchedule: copyJsonObject(baseSchedule),
      comments: comments,
      modifiedByEid: userSession.eid,
      modifiedByFirstName: userSession.firstName,
      modifiedByLastName: userSession.lastName,
      modifiedByWorkgroup: getWorkgroupGivenJobCode(userSession.jobCode),
      createdBy: CLIENT
    };
    await editTempAssignmentEvent(payload)
      .then((res) => res.json())
      .then(async (result) => {
        console.log(result, "result");
        if (result.referenceNumber) {
          setReferenceNumber(result.referenceNumber);
          setIsAddTempAssignmentLoading(false);
          setIsSuccessDialogOpen(true);
          setIsSubmitDialogOpen(false);
        }
      })
      .catch((err) => {
        console.log(err, "error");
        setIsAddTempAssignmentLoading(false);
        setIsSuccessDialogOpen(true);
        setIsErrorAddTempAssignment(true);
        setIsSubmitDialogOpen(false);
      });
  };

  const doesFinalShiftEndOnNextDay = () => {
    const day = getDayOfWeek(endDateRef.current);
    let finalShiftEndHr = baseSchedule[day]?.end?.hr;
    let finalShiftStartHr = baseSchedule[day]?.start?.hr;
    return finalShiftEndHr < finalShiftStartHr;
  };

  const validateEmployeeInfo = async () => {
    let employeeInfoErrorValidation = copyJsonObject(employeeInfoErrorsInitialState);
    setEmployeeInfoErrors(copyJsonObject(employeeInfoErrorsInitialState));
    let isFormValid = false;

    const idErrors = employeeIdErrors !== "" ? employeeIdErrors : validateEmployeeId(employeeID);
    employeeInfoErrorValidation.contactNumberErrors = validatePhoneNumber(
      contactNumber,
      CONTACT_NUMBER_REQUIRED,
      INVALID_CONTACT_NUMBER
    );
    employeeInfoErrorValidation.startDateErrors = validateTAEDStartDate(startDate, endDate);
    employeeInfoErrorValidation.endDateErrors = validateTAEDEndDate(startDate, endDate, stationFilter);
    employeeInfoErrorValidation.commentErrors = validateTAEDComments(comments, isEditTempAssignment); // will need state for isEditTempAssignment

    if (
      employeeInfoErrorValidation.startDateErrors === "" &&
      idErrors === "" &&
      employeeInfoErrorValidation.endDateErrors === ""
    ) {
      employeeInfoErrorValidation.endDateErrors = await validateOverlappingTAED(
        startDate,
        endDate,
        employeeID,
        referenceNumber
      );
    }

    setEmployeeIdErrors(idErrors);
    setEmployeeInfoErrors({ ...employeeInfoErrorValidation });

    if (idErrors !== "") {
      return isFormValid;
    }

    for (const errorType in employeeInfoErrorValidation) {
      if (employeeInfoErrorValidation[errorType] !== "") {
        return isFormValid;
      }
    }

    isFormValid = true;

    return isFormValid;
  };

  const validateContactNumber = () => {
    let employeeInfoErrorValidation = copyJsonObject(employeeInfoErrors);
    employeeInfoErrorValidation.contactNumberErrors = validatePhoneNumber(
      contactNumber,
      CONTACT_NUMBER_REQUIRED,
      INVALID_CONTACT_NUMBER
    );
    setEmployeeInfoErrors({ ...employeeInfoErrorValidation });
  };

  const validateStartDate = () => {
    let employeeInfoErrorValidation = copyJsonObject(employeeInfoErrors);
    if (startDateRef.current) {
      employeeInfoErrorValidation.startDateErrors = validateTAEDStartDate(startDateRef.current, endDateRef.current);
    }
    if (endDateRef.current) {
      employeeInfoErrorValidation.endDateErrors = validateTAEDEndDate(
        startDateRef.current,
        endDateRef.current,
        stationFilter
      );
    }
    setEmployeeInfoErrors({ ...employeeInfoErrorValidation });
  };

  const validateEndDate = () => {
    let employeeInfoErrorValidation = copyJsonObject(employeeInfoErrors);
    if (startDateRef.current) {
      employeeInfoErrorValidation.startDateErrors = validateTAEDStartDate(startDateRef.current, endDateRef.current);
    }
    if (endDateRef.current) {
      employeeInfoErrorValidation.endDateErrors = validateTAEDEndDate(
        startDateRef.current,
        endDateRef.current,
        stationFilter
      );
    }
    setEmployeeInfoErrors({ ...employeeInfoErrorValidation });
  };

  const validateComments = () => {
    let employeeInfoErrorValidation = copyJsonObject(employeeInfoErrors);
    employeeInfoErrorValidation.commentErrors = validateTAEDComments(comments, isEditTempAssignment);
    setEmployeeInfoErrors({ ...employeeInfoErrorValidation });
  };

  const handleCancelConfirmationYesClicked = () => {
    setIsCancelWarningOpen(false);
    clearTempAssignment();
  };

  const handleCancelConfirmationNoClicked = () => {
    setIsCancelWarningOpen(false);
  };

  const handleSubmitConfirmationYesClicked = async () => {
    if (isEditTempAssignment) {
      await editTempAssignment();
    } else {
      await addTempAssignment();
    }
  };

  const handleSubmitConfirmationNoClicked = () => {
    setIsAddTempAssignmentLoading(false);
    setIsSubmitDialogOpen(false);
  };

  const handleSuccessDialogOKClick = () => {
    setIsSuccessDialogOpen(false);
    clearTempAssignment();
  };

  const handleErrorDialogOKClick = () => {
    setIsSuccessDialogOpen(false);
  };

  const validateBaseSchedule = () => {
    let isFormValid = false;
    let setErrorsFlag = false;
    let scheduleErrors = copyJsonObject(baseScheduleErrorsInitialState);
    setBaseScheduleErrors(copyJsonObject(baseScheduleErrorsInitialState));

    scheduleErrors.fullScheduleError = validateNoPartialSchedules(baseSchedule);

    if (scheduleErrors.fullScheduleError !== "") {
      setBaseScheduleErrors(copyJsonObject(scheduleErrors));
      return isFormValid;
    }

    if (employeeData === null) {
      scheduleErrors.fullScheduleError = BASE_SCHEDULE_REQUIRES_EMPLOYEE_ID;
      setBaseScheduleErrors(copyJsonObject(scheduleErrors));
      return isFormValid;
    } else {
      const contract = getUnionContractGivenJobCode(employeeData.jobCode);
      for (const day in baseSchedule) {
        if (isDayIncludedInAssignment(baseSchedule[day])) {
          scheduleErrors[day].error = baseScheduleDailyValidation(baseSchedule, day, contract);
          if (scheduleErrors[day].error !== "") {
            setErrorsFlag = true;
          }
        }
      }
      if (setErrorsFlag) {
        setBaseScheduleErrors(copyJsonObject(scheduleErrors));
        return isFormValid;
      }
    }

    isFormValid = true;

    return isFormValid;
  };

  const areAllValidationsPassing = async () => {
    const isEmployeeInfoValidationPassing = await validateEmployeeInfo();
    const isBaseScheduleValidation = validateBaseSchedule();
    return isEmployeeInfoValidationPassing && isBaseScheduleValidation;
  };

  const onSubmit = async () => {
    setIsValidationLoading(true);

    if (await areAllValidationsPassing()) {
      const formattedPhoneNumber = getFormattedPhoneNumber(contactNumber);
      setContactNumber(formattedPhoneNumber);
      setIsValidationLoading(false);
      setIsSubmitDialogOpen(true);
      // Allow submission
    } else {
      setIsValidationLoading(false);
      // Handle submission refusal
    }
  };

  const breadcrumbsLinks = [HOME_PATH, props.featureConfig.adminPath];
  const confirmationRequired = () => {
    if (
      employeeID !== "" ||
      startDate !== null ||
      endDate !== null ||
      comments !== "" ||
      baseSchedule !== baseScheduleInitialState
    ) {
      return true;
    } else {
      return false;
    }
  };
  const getBreadcrumbsList = () => {
    if (!isEditTempAssignment) {
      const breadcrumbsList = [
        BREADCRUMBS_TITLE.HOME,
        BREADCRUMBS_TITLE.TEMPORARY_ASSIGNMENT_ADMINISTRATION,
        BREADCRUMBS_TITLE.CREATE_TEMPORARY_ASSIGNMENT,
      ];
      return breadcrumbsList;
    } else {
      const breadcrumbsList = [
        BREADCRUMBS_TITLE.HOME,
        BREADCRUMBS_TITLE.TEMPORARY_ASSIGNMENT_ADMINISTRATION,
        BREADCRUMBS_TITLE.EDIT_TEMPORARY_ASSIGNMENT,
      ];
      return breadcrumbsList;
    }
  };
  const getAuditHistory = async (referenceNumber) => {
    await getTAEDEventsByReferenceNumber(referenceNumber)
      .then((result) => {
        setTempAssignmentEvents(result.tempAssignmentEvents);
        setLoading(false);
        setIsAuditHistoryDialogOpen(true);
      })
      .catch((err) => {
        console.log(err, "error");
        setLoading(false);
      });
  };
  const handleAuditHistory = () => {
    setLoading(true);
    getAuditHistory(referenceNumber);
  };
  const renderDesktopView = () => {
    return (
      <Stack sx={{ overflow: "auto" }}>
        <StarsBreadcrumbs
          breadcrumbsList={getBreadcrumbsList()}
          breadcrumbsLinks={breadcrumbsLinks}
          isConfirmationRequired={confirmationRequired()}
          cancelActionType={
            isEditTempAssignment ? CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS : CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA
          }
        />
        <Grid container>
          <Grid item xs={6} sx={{ display: "flex", justifyContent: "start", paddingTop: "10px", paddingLeft: "2rem" }}>
            <Typography variant="h5" component="span">
              {isEditTempAssignment ? "Edit Temporary Assignment" : "Create Temporary Assignment"}
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "2rem" }}>
            {isEditTempAssignment && (
              <Box sx={{ marginRight: "40px", paddingTop: "10px" }}>
                <Button sx={{ width: "145px" }} variant="contained" onClick={handleAuditHistory} disabled={isLoading}>
                  {isLoading ? <CircularProgress size={25} /> : "Audit History"}
                </Button>
              </Box>
            )}
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button
                sx={{ width: "100px" }}
                variant="outlined"
                color="primary"
                onClick={() => {
                  setIsCancelWarningOpen(true);
                }}
              >
                Cancel
              </Button>
            </Box>

            <Box sx={{ paddingLeft: "10px", paddingTop: "10px" }}>
              <Button
                sx={{ width: "100px" }}
                variant="contained"
                color="primary"
                onClick={async () => await onSubmit()}
                disabled={isValidationLoading}
              >
                {isValidationLoading ? <CircularProgress size={25} /> : "Submit"}
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Grid
          container
          sx={{
            paddingTop: "10px",
            justifyContent: "center",
          }}
        >
          <Grid item md={5} lg={4}>
            <Grid container>
              <Grid item xs={6}>
                {userSession.appRole === MULTI_STATION_ADMIN_ACCESS && !isEditTempAssignment ? (
                  <FormControl fullWidth>
                    <StarsSelectInput
                      label="Temp Station"
                      menuMessage="Select Temp Station"
                      value={stationFilter}
                      allOptions={getMultiStationAdminStationList(userSession)}
                      sortComparator={sortOnCaseSensitiveTexts}
                      onChange={(event) => handleStationFilterChange(event)}
                      errors={stationFilter === "" ? STATION_FILTER_MUST_BE_SELECTED : ""}
                      className={classes.inputSelect}
                    />
                  </FormControl>
                ) : (
                  <TextField
                    className={classes.inputText}
                    label="Temp Station"
                    variant="outlined"
                    value={stationFilter}
                    disabled
                  />
                )}
              </Grid>
              <Grid item xs={6}>
                <TextField
                  className={classes.inputText}
                  label="Employee ID"
                  variant="outlined"
                  value={employeeID}
                  onChange={handleEmployeeIdChange}
                  onBlur={handleEmployeeIdBlur}
                  onKeyUp={handleEmployeeIdEnterKeyPressed}
                  error={employeeIdErrors !== ""}
                  helperText={employeeIdErrors}
                  disabled={inputDisable}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item md={6} lg={5}>
            {employeeIdSearchTriggered ? (
              employeeIdSearchLoaded ? (
                employeeData ? (
                  <Grid container sx={{ backgroundColor: "secondary.swaGray1", height: "auto", borderRadius: "5px" }}>
                    <Grid
                      item
                      xs={4}
                      sx={{ fontSize: "24px", paddingTop: "11px", paddingLeft: "16px", textAlign: "left" }}
                    >
                      {employeeData.lastName + ", " + employeeData.firstName}
                    </Grid>

                    <Grid item xs={8} sx={{ textAlign: "left", maxHeight: "56px" }}>
                      <Stack direction="row">
                        <Box ml={2} mr={2}>
                          <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                            HOME STATION
                          </Typography>
                          <p className={classes.employeeDataValue}>{employeeData.homeStation}</p>
                        </Box>
                        <Box ml={2} mr={2}>
                          <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                            WORKGROUP
                          </Typography>
                          <p className={classes.employeeDataValue}>{getWorkgroupGivenJobCode(employeeData.jobCode)}</p>
                        </Box>
                        <Box ml={2} mr={2}>
                          <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                            STATUS
                          </Typography>
                          <p className={classes.employeeDataValue}>{employeeData.ftpt}</p>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                ) : (
                  ""
                )
              ) : (
                <CircularProgress sx={{ float: "left", marginTop: "7px", marginLeft: "10px" }} />
              )
            ) : (
              ""
            )}
          </Grid>
        </Grid>
        {isEditTempAssignment && (
          <Box mt={2}>
            <Alert severity="warning" style={{ paddingLeft: "12%", textAlign: "left" }}>
              <Grid container md={12} lg={12}>
                {TEMP_ASSIGNMENT_EDIT_WARNING_MESSAGE}
              </Grid>
            </Alert>
          </Box>
        )}
        <Grid
          container
          sx={{
            height: "900px",
            marginTop: !isEditTempAssignment ? "15px" : "",
            paddingTop: "10px",
            justifyContent: "center",
            backgroundColor: "secondary.swaGray1",
          }}
        >
          <Grid item md={5} lg={4}>
            <Stack>
              {isEditTempAssignment && (
                <Grid item xs={6}>
                  <Chip className={classes.referenceNumber} label={`Reference #: ${location.state.id}`} />
                </Grid>
              )}
              <Box mt={1} mb={1}>
                <Grid container>
                  <Grid item xs={6}>
                    <StarsDatePicker
                      label="Start Date"
                      date={startDate}
                      onChange={handleStartDateChange}
                      dateErrors={employeeInfoErrors.startDateErrors}
                      className={classes.inputText}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <StarsDatePicker
                      label="End Date"
                      date={endDate}
                      onChange={handleEndDateChange}
                      dateErrors={employeeInfoErrors.endDateErrors}
                      className={classes.inputText}
                      minDate={convert_Luxon_To_DateFns(TWO_MONTHS_IN_PAST(stationFilter))}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box mt={1} mb={1}>
                <Grid container>
                  <Grid item xs={6}>
                    <TextField
                      className={classes.inputText}
                      value={contactNumber}
                      variant="outlined"
                      label="Employee Contact #"
                      onChange={handleContactNumberChange}
                      onBlur={handleContactNumberBlur}
                      error={employeeInfoErrors.contactNumberErrors !== ""}
                      helperText={employeeInfoErrors.contactNumberErrors}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box mt={1} mb={1}>
                <Grid container justifyContent="center">
                  <Alert severity="info" sx={{ marginRight: "10px", textAlign: "left" }}>
                    {TEMP_ASSIGNMENT_CLOCK_LOCATION_MESSAGE}
                  </Alert>
                </Grid>
              </Box>
              <Box mt={1} mb={1}>
                <TextField
                  className={classes.inputText}
                  value={comments}
                  variant="outlined"
                  onChange={handleCommentsChange}
                  onBlur={handleCommentsBlur}
                  label="Comments"
                  multiline
                  rows={8}
                  error={!!employeeInfoErrors.commentErrors}
                  helperText={employeeInfoErrors.commentErrors}
                />
                {comments.length > FREEFORM_REASON_CHAR_LIMIT_FOR_TEMP_ASSGNMENT ? (
                  <Box sx={{ textAlign: "right", marginRight: "14px", color: "primary.swaRed" }}>
                    {comments.length} / {FREEFORM_REASON_CHAR_LIMIT_FOR_TEMP_ASSGNMENT}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "right", marginRight: "14px" }}>
                    {comments.length} / {FREEFORM_REASON_CHAR_LIMIT_FOR_TEMP_ASSGNMENT}
                  </Box>
                )}
              </Box>
            </Stack>
          </Grid>
          <Grid item md={6} lg={5}>
            <Box mt={1} mb={1} display="flex" flexDirection="row-reverse">
              <BaseScheduleDialog
                startDate={startDate}
                endDate={endDate}
                openBaseSchedule={true}
                baseSchedule={baseSchedule}
                baseScheduleErrors={baseScheduleErrors}
                handleStartHourChange={handleStartHourChange}
                handleStartMinChange={handleStartMinChange}
                handleEndHourChange={handleEndHourChange}
                handleEndMinChange={handleEndMinChange}
                handleClearBaseSchedule={handleClearBaseSchedule}
                handleCheckedShiftOff={handleCheckedShiftOff}
                handleApplyToAll={handleApplyToAll}
                applyToAllFilterData={applyToAllFilterData}
                handleApplyToAllStartHourChange={handleApplyToAllStartHourChange}
                handleApplyToAllStartMinChange={handleApplyToAllStartMinChange}
                handleApplyToAllEndHourChange={handleApplyToAllEndHourChange}
                handleApplyToAllEndMinChange={handleApplyToAllEndMinChange}
              />
            </Box>
          </Grid>
        </Grid>
        {isCancelWarningOpen ? (
          <ConfirmationDialog
            isDialogOpen={isCancelWarningOpen}
            message={
              isEditTempAssignment
                ? ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS)
                : ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA)
            }
            redirectTo={props.featureConfig.adminPath}
            handleNoButtonClicked={handleCancelConfirmationNoClicked}
            handleYesButtonClicked={handleCancelConfirmationYesClicked}
          />
        ) : (
          ""
        )}
        {isSubmitDialogOpen ? (
          <>
            <ConfirmationDialog
              isDialogOpen={isSubmitDialogOpen}
              message={
                isEditTempAssignment
                  ? TEMP_ASSIGNMENT_EDIT_CONFIRM_MESSAGE(
                      employeeData.firstName,
                      employeeData.lastName,
                      employeeData.eid
                    )
                  : TEMP_ASSIGNMENT_CREATE_CONFIRM_MESSAGE(
                      employeeData.firstName,
                      employeeData.lastName,
                      employeeData.eid
                    )
              }
              handleNoButtonClicked={handleSubmitConfirmationNoClicked}
              handleYesButtonClicked={handleSubmitConfirmationYesClicked}
              isYesLoading={isAddTempAssignmentLoading}
              hasWarning={doesFinalShiftEndOnNextDay()}
              warningMessage={
                "The final shift of the temporary assignment ends on the next day. Please confirm that the selected dates and base schedule are correct."
              }
              warningTitle={WARNING_ALERT_TITLE}
            />
          </>
        ) : (
          ""
        )}
        {isSuccessDialogOpen ? (
          <SuccessDialog
            isSuccessDialogOpen={isSuccessDialogOpen}
            title={isErrorAddTempAssignment ? TEMP_ASSIGNMENT_ACTION_ERROR_TITLE : TEMP_ASSIGNMENT_CONFIRMATION_TITLE}
            subTitle={isErrorAddTempAssignment ? undefined : REFERENCE_NUMBER_SUB_TITLE(referenceNumber)}
            message={
              isEditTempAssignment
                ? TEMP_ASSIGNMENT_EDIT_SUCCESS_MESSAGE(employeeData.firstName, employeeData.lastName, employeeData.eid)
                : TEMP_ASSIGNMENT_CREATE_SUCCESS_MESSAGE(
                    employeeData.firstName,
                    employeeData.lastName,
                    employeeData.eid
                  )
            }
            redirectTo={isErrorAddTempAssignment ? undefined : props.featureConfig.adminPath}
            handleOKClick={isErrorAddTempAssignment ? handleErrorDialogOKClick : handleSuccessDialogOKClick}
            hasError={isErrorAddTempAssignment}
            error={TEMP_ASSIGNMENT_SUBMISSION_ERROR_MESSAGE}
          />
        ) : (
          ""
        )}{" "}
        {isAuditHistoryDialogOpen && (
          <TemporaryAssignmentAuditHistoryDialog
            isDialogOpen={isAuditHistoryDialogOpen}
            setDialogOpen={setIsAuditHistoryDialogOpen}
            tempAssignmentEvents={tempAssignmentEvents}
          />
        )}
      </Stack>
    );
  };

  const renderMobileView = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div className="App-contents" data-testid="create-temporary-assignment">
      {isUserAuthorizedStateLoading ? <PageLoading /> : props.isDesktopView ? renderDesktopView() : renderMobileView()}
    </div>
  );
};

export default CreateTemporaryAssignmentPage;
